'use client'

import { imageQuality } from '@repo/sanity/image'
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPlugin, CarouselPrevious } from '@repo/ui/components/carousel'
import { cn } from '@repo/ui/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import { Image } from 'next-sanity/image'
import { FunctionComponent, useEffect, useState } from 'react'
import { PageSectionProps } from '../../../types/page.types'

interface Props {
  store: NonNullable<NonNullable<PageSectionProps<'sectionStores'>>['section']>['stores'][number]
  index: number
}

const StoreImagesCarousel: FunctionComponent<Props> = ({ store, index }) => {
  const [api, setApi] = useState<CarouselApi>()
  const images = store?.images || []
  const imageCount = images.length

  const plugins: CarouselPlugin = [
    Autoplay({
      delay: 5000,
      playOnInit: false,
    }),
  ]

  useEffect(() => {
    const timer = setTimeout(
      () => {
        const snapsCount = (api?.scrollSnapList() || []).length

        // We can only enable autoplay if there are slides; otherwise, Embla gives an error.
        if ((window.innerWidth < 1024 && snapsCount > 1) || (window.innerWidth >= 1024 && snapsCount > 3)) {
          const autoplay = api?.plugins()?.autoplay

          if (autoplay && !autoplay.isPlaying()) {
            autoplay.play()
          }
        }
      },
      1000 * (index + 1),
    )

    return () => clearTimeout(timer)
  }, [api, index])

  if (imageCount === 0) {
    return null
  }

  return (
    <Carousel setApi={setApi} plugins={plugins}>
      <CarouselContent>
        {images.flatMap((image, i) => {
          if (!image?.asset?.url) {
            return []
          }

          return (
            <CarouselItem key={image._key} className='relative aspect-[2/3] w-full lg:basis-1/3'>
              <Image
                fill={true}
                quality={imageQuality}
                className='object-cover'
                priority={index === 0 && i === 0}
                src={image?.asset?.url}
                alt={image?.alt || store.title || ''}
                blurDataURL={image?.asset?.metadata?.lqip ?? undefined}
                placeholder={image?.asset?.metadata?.lqip ? 'blur' : undefined}
                sizes='(min-width: 1024px) 33vw, 100vw'
              />
            </CarouselItem>
          )
        })}
      </CarouselContent>
      {imageCount > 1 && (
        <>
          <CarouselPrevious className={cn(imageCount <= 3 && 'lg:hidden')} />
          <CarouselNext className={cn(imageCount <= 3 && 'lg:hidden')} />
        </>
      )}
    </Carousel>
  )
}

export default StoreImagesCarousel
